import { useState } from "react";
import { Link } from "react-router-dom";
import { Mail, MapPin, Phone, Send, ArrowLeft, Loader2 } from "lucide-react";
import { submitQuote } from "@/lib/api";
import { toast } from "sonner";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { useLanguage } from "@/i18n/LanguageContext";

const CONTACT_EMAIL = "contact@smartxglass.ch";

export default function Contact() {
  const { t } = useLanguage();
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);

  const handleChange = (k) => (e) => setForm((prev) => ({ ...prev, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error(t("contact.errorRequired"));
      return;
    }
    setLoading(true);
    try {
      await submitQuote(form);
      toast.success(t("contact.sent"));
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch {
      toast.error(`${t("contact.errorSend")} ${CONTACT_EMAIL}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <main data-testid="contact-page" className="bg-[#0a0a0a] text-[#f4ede0]">
      <Navbar onCartClick={() => (window.location.href = "/#configurateur")} />

      <section className="luxe-bg pt-32 pb-20">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-12">
          <Link to="/" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-[#cbc1ad] hover:text-[#d89557] transition mb-10">
            <ArrowLeft className="w-4 h-4" /> {t("contact.back")}
          </Link>

          <div className="grid lg:grid-cols-12 gap-12 sm:gap-16">
            <div className="lg:col-span-5 fade-up">
              <span className="text-xs uppercase tracking-[0.32em] text-[#d89557]">{t("contact.label")}</span>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[#f4ede0] mt-4 sm:mt-6 leading-[1.05]">
                {t("contact.title1")} <span className="italic copper-text">{t("contact.title2")}</span>.
              </h1>
              <p className="text-[#cbc1ad] mt-8 text-base leading-relaxed max-w-md">{t("contact.desc")}</p>

              <div className="mt-12 space-y-6">
                <InfoRow icon={Mail} label={t("contact.email")}>
                  <a data-testid="contact-email-link" href={`mailto:${CONTACT_EMAIL}`} className="text-[#f4ede0] hover:text-[#d89557] transition text-base sm:text-lg break-all">
                    {CONTACT_EMAIL}
                  </a>
                </InfoRow>
                <InfoRow icon={MapPin} label={t("contact.address")}>
                  <span className="text-[#f4ede0]">{t("contact.addressVal")}</span>
                </InfoRow>
                <InfoRow icon={Phone} label={t("contact.hours")}>
                  <span className="text-[#f4ede0]">{t("contact.hoursVal")}</span>
                </InfoRow>
              </div>

              <div className="hairline mt-14 mb-6" />
              <p className="text-xs text-[#7a716a] uppercase tracking-[0.25em]">{t("contact.reply")}</p>
            </div>

            <div className="lg:col-span-7 fade-up delay-2">
              <form onSubmit={submit} data-testid="contact-form" className="glass-card p-6 sm:p-8 lg:p-10 space-y-6">
                <div className="grid md:grid-cols-2 gap-5">
                  <Field label={t("contact.name")} testid="contact-name" value={form.name} onChange={handleChange("name")} />
                  <Field label={t("contact.emailField")} type="email" testid="contact-email" value={form.email} onChange={handleChange("email")} />
                </div>
                <Field label={t("contact.phone")} testid="contact-phone" value={form.phone} onChange={handleChange("phone")} />
                <div>
                  <label className="block text-[10px] uppercase tracking-[0.25em] text-[#7a716a] mb-2">{t("contact.message")}</label>
                  <textarea
                    data-testid="contact-message"
                    rows={6}
                    value={form.message}
                    onChange={handleChange("message")}
                    placeholder={t("contact.msgPlaceholder")}
                    className="w-full bg-[#0a0a0a] border border-[#2a2422] focus:border-[#b87333] outline-none px-4 py-3 text-[#f4ede0] placeholder:text-[#7a716a] transition resize-none"
                  />
                </div>
                <button type="submit" disabled={loading} data-testid="contact-submit" className="btn-copper w-full py-4 text-sm uppercase tracking-[0.22em] inline-flex items-center justify-center gap-3 disabled:opacity-50">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  {t("contact.submit")}
                </button>
                <p className="text-[10px] text-center text-[#7a716a] uppercase tracking-[0.22em]">
                  {t("contact.orWriteTo")}{" "}
                  <a href={`mailto:${CONTACT_EMAIL}`} className="text-[#d89557] hover:underline">{CONTACT_EMAIL}</a>
                </p>
              </form>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}

const InfoRow = ({ icon: Icon, label, children }) => (
  <div className="flex items-start gap-4">
    <div className="w-10 h-10 rounded-sm border border-[#b87333]/40 flex items-center justify-center flex-shrink-0">
      <Icon className="w-4 h-4 text-[#d89557]" />
    </div>
    <div>
      <div className="text-[10px] uppercase tracking-[0.28em] text-[#7a716a] mb-1">{label}</div>
      {children}
    </div>
  </div>
);

const Field = ({ label, type = "text", value, onChange, testid }) => (
  <div>
    <label className="block text-[10px] uppercase tracking-[0.25em] text-[#7a716a] mb-2">{label}</label>
    <input data-testid={testid} type={type} value={value} onChange={onChange} className="w-full bg-[#0a0a0a] border border-[#2a2422] focus:border-[#b87333] outline-none px-4 py-3 text-[#f4ede0] transition" />
  </div>
);
